import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import Button from "../../components/Button/Button";
import KpiCard from "../../components/KpiCard/KpiCard";
import { Tag } from "../../components/Tag/Tag";

import { useToast } from "../../context/ToastContext";
import { getColorBaseTokenValue } from "../../design-tokens";

import ProductionApi, { HourlyProductionResponse } from "../../services/ProductionApi";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import useWorkSchedule, { useShiftList } from "../../hooks/useWorkSchedule";
import ShiftFilter from "../../components/ShiftFilter/ShiftFilter";
import { useActivityTracking, useCoalescedActivityTracking } from "../../hooks/useActivityTracking";
import { ActivityEventType } from "../../utilities/activityLog";
import { isOffShiftHour, localHour, OFF_SHIFT_PROD_COLOR } from "../../utilities/workSchedule";
import HourlyChart from "./HourlyChart";
import styles from "./ReportHourlyProduction.module.css";

type SortKey = "hour" | "produced" | "target" | "diff";
type SortDir = "asc" | "desc";

interface Row {
  hour: number;
  produced: number;
  target: number;
  diff: number;
  offShift: boolean;
  /** Ordem cronológica vinda do backend (para turno que cruza a meia-noite: 20,21,22,23,0,1). */
  seq: number;
}

const pad = (n: number): string => String(n).padStart(2, "0");
const toYmd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, days: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c;
};
const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const ReportHourlyProduction: React.FC = () => {
  const { t, i18n } = useTranslation("hourlyProduction");
  const { displayToast } = useToast();
  // Coalescido: navegar três dias seguidos gera UM evento, com o dia em que parou.
  const trackDay = useCoalescedActivityTracking();
  const track = useActivityTracking();
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";
  // Jornada completa (todas as janelas) para o sombreado fora-de-turno em "Todos". Com um turno
  // filtrado, o backend já devolve só as horas do turno (todas dentro dele → sem cinza).
  const schedule = useWorkSchedule();
  const shifts = useShiftList();
  const [shiftId, setShiftId] = useState<string | null>(null);

  const [data, setData] = useState<HourlyProductionResponse | null>(null);
  const [loading, setLoading] = useState(true); // já há um load pendente no mount — evita flash do estado vazio
  const [lastUpdated, setLastUpdated] = useState("");
  const [day, setDay] = useState<Date>(() => startOfDay(new Date()));
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "hour", dir: "asc" });

  // Calculado a fresco a cada render (não congela no mount → correto ao virar a meia-noite,
  // inclusive para o gate do refresh em tempo real e o passo do dia).
  const today = startOfDay(new Date());
  const isToday = sameDay(day, today);
  const atLatest = day.getTime() >= today.getTime();

  const fmt = useCallback(
    (v: number, dec = 0): string =>
      Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec }),
    [locale],
  );

  const load = useCallback(
    async (d: Date, sid: string | null): Promise<void> => {
      setLoading(true);
      try {
        const resp = await ProductionApi.GetHourlyProduction(toYmd(d), sid ?? undefined);
        setData((resp?.data as HourlyProductionResponse) ?? null);
        setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }));
      } catch (err: any) {
        displayToast("error", t("loadErrorTitle"), err?.errors?.[0] ?? t("loadErrorMsg"), false, 6000);
      } finally {
        setLoading(false);
      }
    },
    [displayToast, t, locale],
  );

  useEffect(() => {
    void load(day, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data local em yyyy-MM-dd para o payload do log. Getters LOCAIS de propósito: o dado da
  // POC é dia-calendário local, e toISOString() deslocaria o dia nas seleções da noite.
  const dayPayload = (d: Date): Record<string, unknown> => ({
    day: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
  });

  const goDay = (delta: number): void => {
    const next = addDays(day, delta);
    if (next.getTime() > today.getTime()) return;
    setDay(next);
    trackDay(ActivityEventType.DayChange, dayPayload(next));
    void load(next, shiftId);
  };
  const goToday = (): void => {
    setDay(today);
    // Voltar para hoje é troca de dia como qualquer outra. Sem isto, o log mostraria o usuário
    // parado no dia antigo pelo resto da sessão.
    trackDay(ActivityEventType.DayChange, dayPayload(today));
    void load(today, shiftId);
  };
  const handleShiftChange = (id: string | null): void => {
    // Mesmo evento das 4 telas de relatório: sem ele, uma consulta do tipo "que turnos os
    // usuários acompanham" excluiria esta tela em silêncio.
    track(ActivityEventType.ShiftChange, { shiftId: id });
    setShiftId(id);
    void load(day, id);
  };
  // Tempo real via SignalR só quando vendo HOJE (dias passados não mudam).
  useLiveRefresh(() => void load(day, shiftId), isToday);

  const dayLabel = useMemo(
    () => day.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }),
    [day, locale],
  );

  const derived = useMemo(() => {
    const series = data?.series ?? [];
    const hourlyTarget = data?.hourlyTarget ?? 0;
    const hasData = !!data?.hasData && series.length > 0;

    const rows: Row[] = series.map((p, idx) => {
      const target = p.target ?? hourlyTarget;
      return { hour: p.hour, produced: p.produced, target, diff: p.produced - target, offShift: isOffShiftHour(p.hour, schedule), seq: idx };
    });

    // KPIs escopados ao TURNO (Q2: números que o front consegue). Horas fora do turno não têm
    // meta legítima → contam só o produzido, mostrado à parte como "fora de turno".
    const inShift = rows.filter((r) => !r.offShift);
    const totalProduced = inShift.reduce((a, r) => a + r.produced, 0);
    const totalTarget = inShift.reduce((a, r) => a + r.target, 0);
    const attainment = totalTarget > 0 ? (totalProduced / totalTarget) * 100 : 0;
    const offShiftProduced = rows.filter((r) => r.offShift).reduce((a, r) => a + r.produced, 0);

    let peak = { hour: -1, produced: 0 };
    rows.forEach((r) => {
      if (r.produced > peak.produced) peak = { hour: r.hour, produced: r.produced };
    });

    // Hora corrente no fuso da POC, pois r.hour vem como hora local do backend.
    const nowHour = localHour(new Date(), schedule.tzOffsetHours);
    const nowIndex = data && sameDay(day, today) ? rows.findIndex((r) => r.hour === nowHour) : -1;

    return { rows, hourlyTarget, hasData, totalProduced, totalTarget, attainment, offShiftProduced, peak, nowIndex, hoursCount: inShift.length };
  }, [data, day, today, schedule]);

  const sortedRows = useMemo(() => {
    const rows = [...derived.rows];
    const sign = sort.dir === "asc" ? 1 : -1;
    // Ordenar por "Hora" segue a ordem CRONOLÓGICA do turno (seq do backend), não o valor numérico
    // da hora — assim um turno que cruza a meia-noite (20,21,22,23,0,1) não é reembaralhado p/ 0,1,20…
    const val = (r: Row): number => (sort.key === "hour" ? r.seq : r[sort.key]);
    rows.sort((a, b) => (val(a) - val(b)) * sign);
    return rows;
  }, [derived.rows, sort]);

  const toggleSort = (key: SortKey): void =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "hour" ? "asc" : "desc" }));

  const sortArrow = (key: SortKey): string | null =>
    sort.key !== key ? null : sort.dir === "asc" ? "arrow_upward" : "arrow_downward";

  const colorSuccess = getColorBaseTokenValue("color.feedback.success.500");
  const colorDanger = getColorBaseTokenValue("color.feedback.danger.500");

  const colorOffShift = getColorBaseTokenValue("color.neutral.500"); // Cinza 500 — NPP (fora de turno)
  const chartColors = useMemo(
    () => ({
      // Fora de turno COM produção → violeta (produção fora de turno); fora de turno sem
      // produção → cinza; dentro do turno → verde/vermelho por atingimento.
      bars: derived.rows.map((r) =>
        r.offShift
          ? r.produced > 0
            ? OFF_SHIFT_PROD_COLOR
            : colorOffShift
          : r.produced >= r.target
            ? colorSuccess
            : colorDanger,
      ),
      meta: getColorBaseTokenValue("color.primary.500"),
      now: getColorBaseTokenValue("color.secondary.500"),
      text: getColorBaseTokenValue("color.neutral.500"),
      grid: getColorBaseTokenValue("color.neutral.100"),
      offShiftBand: "rgba(107, 114, 128, 0.12)",
    }),
    [derived.rows, colorSuccess, colorDanger, colorOffShift],
  );

  const hourLabel = (h: number): string => `${pad(h)}h`;

  const diffCell = (diff: number): React.ReactNode => {
    if (diff > 0) return <span className={`${styles.diffPill} ${styles.diffPos}`}>+{fmt(diff)}</span>;
    if (diff < 0) return <span className={`${styles.diffPill} ${styles.diffNeg}`}>{fmt(diff)}</span>;
    return <span className={styles.diffZero}>0</span>;
  };

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <Tag
          label={lastUpdated ? `${t("updatedAt")} ${lastUpdated}` : t("loadingLabel")}
          severity="successInverse"
          icon="sync"
        />
      </PageHeader>

      {/* Filtro: seletor de dia */}
      <div className={`${styles.card} ${styles.filters}`}>
        <div className={styles.filterGroup}>
          <span className={styles.fieldLabel}>{t("fDay")}</span>
          <div className={styles.dayStepper}>
            <button type="button" className={styles.dayNav} onClick={() => goDay(-1)} aria-label={t("prevDay")}>
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className={styles.dayLabel}>
              <span className="material-symbols-outlined">calendar_month</span>
              {dayLabel}
            </span>
            <button
              type="button"
              className={styles.dayNav}
              onClick={() => goDay(1)}
              disabled={atLatest}
              aria-label={t("nextDay")}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
        <ShiftFilter value={shiftId} onChange={handleShiftChange} shifts={shifts} />
        {isToday ? (
          <Tag label={t("liveLabel")} severity="successInverse" icon="bolt" />
        ) : (
          <Button label={t("goToday")} icon="today" severity="secondary" variant="outlined" onClick={goToday} loading={loading} />
        )}
      </div>

      {loading && !data ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
        </div>
      ) : derived.hasData ? (
        <>
          {/* KPIs */}
          <div className={styles.kpiRow}>
            <KpiCard
              title={t("kProduced")}
              value={fmt(derived.totalProduced)}
              icon="inventory_2"
              variant="primary"
              descriptionIcon={derived.attainment >= 100 ? "arrow_upward" : "arrow_downward"}
              trend={derived.attainment >= 100 ? "up" : "down"}
              description={`${fmt(derived.attainment, 0)}% ${t("ofTarget")}${derived.offShiftProduced > 0 ? ` · +${fmt(derived.offShiftProduced)} ${t("offShiftSuffix")}` : ""}`}
            />
            <KpiCard
              title={t("kTarget")}
              value={fmt(derived.totalTarget)}
              icon="flag"
              variant="info"
              descriptionIcon="schedule"
              trend="neutral"
              description={`${fmt(derived.hourlyTarget, 1)}/${t("perHour")} · ${derived.hoursCount} ${t("hoursShort")}`}
            />
            <KpiCard
              title={t("kPeak")}
              value={fmt(derived.peak.produced)}
              icon="trending_up"
              variant="success"
              descriptionIcon="schedule"
              trend="neutral"
              description={derived.peak.hour >= 0 ? `${t("atHour")} ${hourLabel(derived.peak.hour)}` : "—"}
            />
          </div>

          {/* Gráfico + tabela lado a lado */}
          <div className={styles.twoCol}>
            <section className={styles.card}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>
                  <span className="material-symbols-outlined">bar_chart</span>
                  {t("chartTitle")}
                </span>
                <div className={styles.legend}>
                  <span className={styles.legendItem}>
                    <span className={styles.legendSwatch} style={{ background: colorSuccess }} />
                    {t("legAbove")}
                  </span>
                  <span className={styles.legendItem}>
                    <span className={styles.legendSwatch} style={{ background: colorDanger }} />
                    {t("legBelow")}
                  </span>
                  <span className={styles.legendItem}>
                    <span className={styles.legendLine} />
                    {t("legTarget")}
                  </span>
                  <span className={styles.legendItem}>
                    <span className={styles.legendSwatch} style={{ background: colorOffShift }} />
                    {t("legOffShift")}
                  </span>
                  <span className={styles.legendItem}>
                    <span className={styles.legendSwatch} style={{ background: OFF_SHIFT_PROD_COLOR }} />
                    {t("legOffShiftProd")}
                  </span>
                </div>
              </div>
              <div className={styles.chartBody}>
                <HourlyChart
                  labels={derived.rows.map((r) => hourLabel(r.hour))}
                  produced={derived.rows.map((r) => r.produced)}
                  target={derived.hourlyTarget}
                  offShift={derived.rows.map((r) => r.offShift)}
                  colors={chartColors}
                  metaLabel={t("legTarget")}
                  producedLabel={t("colProduced")}
                  nowIndex={derived.nowIndex}
                  nowLabel={t("nowLabel")}
                  locale={locale}
                  height={320}
                />
              </div>
            </section>

            <section className={styles.card}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>
                  <span className="material-symbols-outlined">table_rows</span>
                  {t("tableTitle")}
                </span>
                <span className={styles.sectionCount}>{derived.hoursCount} {t("hoursShort")}</span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={`${styles.th} ${styles.thLeft}`}>
                        <button type="button" className={`${styles.sortBtn} ${styles.sortBtnLeft}`} onClick={() => toggleSort("hour")}>
                          {t("colHour")}
                          {sortArrow("hour") && <span className={`material-symbols-outlined ${styles.sortArrow}`}>{sortArrow("hour")}</span>}
                        </button>
                      </th>
                      <th className={styles.th}>
                        <button type="button" className={styles.sortBtn} onClick={() => toggleSort("produced")}>
                          {t("colProduced")}
                          {sortArrow("produced") && <span className={`material-symbols-outlined ${styles.sortArrow}`}>{sortArrow("produced")}</span>}
                        </button>
                      </th>
                      <th className={styles.th}>
                        <button type="button" className={styles.sortBtn} onClick={() => toggleSort("target")}>
                          {t("colTarget")}
                          {sortArrow("target") && <span className={`material-symbols-outlined ${styles.sortArrow}`}>{sortArrow("target")}</span>}
                        </button>
                      </th>
                      <th className={styles.th}>
                        <button type="button" className={styles.sortBtn} onClick={() => toggleSort("diff")}>
                          {t("colDiff")}
                          {sortArrow("diff") && <span className={`material-symbols-outlined ${styles.sortArrow}`}>{sortArrow("diff")}</span>}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => (
                      <tr key={r.hour} style={r.offShift ? { opacity: 0.55 } : undefined}>
                        <td className={`${styles.td} ${styles.tdHour}`}>
                          {hourLabel(r.hour)}
                          {r.offShift && (
                            <span style={{ fontSize: "0.7rem", marginLeft: 4, color: r.produced > 0 ? OFF_SHIFT_PROD_COLOR : undefined }}>
                              · {r.produced > 0 ? t("offShiftProdTag") : t("offShiftTag")}
                            </span>
                          )}
                        </td>
                        <td className={`${styles.td} ${styles.tdProd}`}>{fmt(r.produced)}</td>
                        <td className={`${styles.td} ${styles.tdMeta}`}>{r.offShift ? "—" : fmt(r.target, 1)}</td>
                        <td className={styles.td}>{r.offShift ? "—" : diffCell(r.diff)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={styles.tfootRow}>
                      <td className={styles.tfootLabel}>{t("dayTotal")}</td>
                      <td>{fmt(derived.totalProduced)}</td>
                      <td>{fmt(derived.totalTarget, 1)}</td>
                      <td>{diffCell(derived.totalProduced - derived.totalTarget)}</td>
                    </tr>
                    {derived.offShiftProduced > 0 && (
                      <tr className={styles.tfootRow}>
                        <td className={styles.tfootLabel}>{t("offShiftRow")}</td>
                        <td>{fmt(derived.offShiftProduced)}</td>
                        <td>—</td>
                        <td>—</td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </section>
          </div>
        </>
      ) : (
        <div className={styles.card}>
          <div className={styles.empty}>
            <span className={`material-symbols-outlined ${styles.emptyIcon}`}>bar_chart_off</span>
            <div>
              <div className={styles.emptyTitle}>{t("emptyTitle")}</div>
              <div className={styles.emptySub}>{t("emptySub")}</div>
            </div>
            {!isToday && <Button label={t("goToday")} icon="today" severity="neutral" variant="outlined" onClick={goToday} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportHourlyProduction;
